import { useIntl } from 'react-intl';
import { getDoohAdTypeFromAdFormat } from 'sonata-static-storage/lib/helpers/ads.helpers';
import {
  getDoohAdFormatsCatalog,
  getSupportedScreenIds,
  getValidResolutionsFromAspectRatio
} from 'sonata-static-storage/lib/helpers/creative.helpers';
import { normalize } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import './adTypeScreens.scss';

import type { DoohAdFormat, WizardFormattedScreen } from 'sonata-static-storage/lib/common/common.types';
import type { AdInterface } from 'sonata-static-storage/lib/schemas/ad/ad.schema';
import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { Creative } from 'sonata-static-storage/lib/schemas/creative/creative.schema';

type Props = {
  ad: AdInterface<string>;
  creative?: Creative;
  currencyInfo: BaseCurrencyInterface;
  entityFamily?: CampaignFamilyEnum;
  screens?: WizardFormattedScreen[];
};

export function AdTypeScreens({
  ad,
  creative,
  currencyInfo,
  entityFamily,
  screens = []
}: Readonly<Props>): JSX.Element {
  const { formatMessage } = useIntl();

  const { adFormat } = ad;
  const adType = getDoohAdTypeFromAdFormat(adFormat as DoohAdFormat);
  const aspectRatios = getDoohAdFormatsCatalog(screens, adType);

  let uniqueSupportedScreenIds = 0;
  if (creative?.width && creative?.height) {
    const aspectRatio = aspectRatios.find(({ id }) => creative.aspectRatio === id);
    let validResolutions = [];
    if (aspectRatio) {
      validResolutions = getValidResolutionsFromAspectRatio(aspectRatio, parseInt(creative.width));
      uniqueSupportedScreenIds = getSupportedScreenIds(aspectRatio, validResolutions);
    }
  }

  const adTypeLabel = formatMessage({
    defaultMessage: 'Type',
    id: `common.doohAdTypes.${adType}`
  });

  const screenCountLabel = formatMessage(
    {
      defaultMessage: 'Screen(s)',
      id: 'multiflight.creatives.table.screens.number'
    },
    { screens: normalize(uniqueSupportedScreenIds, 0, currencyInfo.countries?.[0]) }
  );

  const titleParts = [adTypeLabel];
  if (entityFamily === CampaignFamilyEnum.dooh && creative?.width && creative?.height && creative.aspectRatio) {
    titleParts.push(creative.aspectRatio);
  }
  titleParts.push(screenCountLabel);
  const title = titleParts.join(' ');

  return (
    <div className="adTypeScreens" title={title}>
      {adTypeLabel}
      {entityFamily === CampaignFamilyEnum.dooh && creative?.width && creative?.height && <> {creative.aspectRatio}</>}
      <span className="numberOfScreens"> {screenCountLabel}</span>
    </div>
  );
}
